import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Database, ShieldAlert, ShieldCheck, Play,
  Check, X, AlertTriangle, Sparkles, FileWarning, Lock, Ban,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_BADGE = {
  critical: 'bg-rose-100 text-rose-800 border-rose-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  medium: 'bg-amber-100 text-amber-800 border-amber-300',
  low: 'bg-emerald-100 text-emerald-800 border-emerald-300',
};

const VERDICT_BADGE = {
  winning: 'bg-emerald-100 text-emerald-800',
  neutral: 'bg-amber-100 text-amber-800',
  losing: 'bg-rose-100 text-rose-800',
};

function StatBox({ label, value, hint, testId }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-light tabular-nums mt-1">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function eur(v) {
  if (v == null) return '—';
  return `€${Number(v).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function GateRow({ label, ok, current, required, testId }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-border last:border-b-0" data-testid={testId}>
      <div className="flex items-start gap-2 min-w-0">
        {ok ? <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> : <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />}
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{label}</div>
          <div className="text-[11px] text-muted-foreground">vereist: {required}</div>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground text-right shrink-0 max-w-[40%] break-words">
        {typeof current === 'object' ? JSON.stringify(current) : String(current ?? '—')}
      </div>
    </div>
  );
}

function RecommendationCard({ rec, onApprove, onReject, busy }) {
  const sev = SEV_BADGE[rec.severity] || SEV_BADGE.low;
  const ev = rec.evidence || {};
  return (
    <div className="rounded-xl border border-border bg-white p-4 space-y-3" data-testid={`ads-rec-card-${rec.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className={`text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${sev}`}>
            {rec.severity}
          </span>
          <span className="ml-2 text-xs text-muted-foreground">{rec.rule_id}</span>
          <span className="ml-2 text-xs text-muted-foreground">· {rec.type}</span>
        </div>
        <span className="text-[10px] uppercase rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">
          {rec.status}
        </span>
      </div>
      <div className="text-sm font-medium">{rec.reason}</div>
      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
        {Object.entries(rec.entity_path || {}).slice(0, 4).map(([k, v]) => (
          <div key={k}><span className="font-mono">{k}:</span> {String(v)}</div>
        ))}
        {ev.spend_eur != null && <div>Spend: {eur(ev.spend_eur)}</div>}
        {ev.clicks != null && <div>Clicks: {ev.clicks}</div>}
        {ev.impressions != null && <div>Impressies: {ev.impressions}</div>}
        {ev.ctr_pct != null && <div>CTR: {ev.ctr_pct}%</div>}
      </div>
      <div className="text-xs"><span className="text-muted-foreground">Verwacht effect:</span> {rec.expected_effect}</div>
      {rec.status === 'pending' && (
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => onApprove(rec)}
            disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs hover:bg-emerald-700 disabled:opacity-50"
            data-testid={`ads-rec-approve-${rec.id}`}
          >
            <Check className="w-3 h-3" /> Approve (simulated)
          </button>
          <button
            onClick={() => onReject(rec)}
            disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs hover:bg-slate-200 disabled:opacity-50"
            data-testid={`ads-rec-reject-${rec.id}`}
          >
            <X className="w-3 h-3" /> Reject
          </button>
        </div>
      )}
      {rec.status === 'approved_simulated' && (
        <div className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Gesimuleerd goedgekeurd door {rec.approved_by} — geen echte Ads-mutatie
        </div>
      )}
      {rec.status === 'rejected' && (
        <div className="text-[11px] text-rose-700">Afgewezen door {rec.rejected_by}: {rec.rejection_reason}</div>
      )}
    </div>
  );
}

export default function AdsControlAgentTab({ token }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [health, setHealth] = useState(null);
  const [summary, setSummary] = useState(null);
  const [healthScores, setHealthScores] = useState({ items: [] });
  const [keywords, setKeywords] = useState({ items: [] });
  const [searchTerms, setSearchTerms] = useState({ items: [] });
  const [recs, setRecs] = useState({ items: [] });
  const [draft, setDraft] = useState(null);
  const [actions, setActions] = useState({ items: [] });
  const [gates, setGates] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [h, sum, hs, kw, st, r, d, act, g] = await Promise.all([
        axios.get(`${API}/admin/ads-control/health`, { headers }),
        axios.get(`${API}/admin/ads-control/metrics/summary`, { headers }),
        axios.get(`${API}/admin/ads-control/health-scores`, { headers }),
        axios.get(`${API}/admin/ads-control/metrics/keywords?limit=50`, { headers }),
        axios.get(`${API}/admin/ads-control/metrics/search-terms`, { headers }),
        axios.get(`${API}/admin/ads-control/recommendations?limit=50`, { headers }),
        axios.get(`${API}/admin/ads-control/draft`, { headers }),
        axios.get(`${API}/admin/ads-control/action-history?limit=20`, { headers }),
        axios.get(`${API}/admin/ads-control/autopilot/gates`, { headers }),
      ]);
      setHealth(h.data);
      setSummary(sum.data);
      setHealthScores(hs.data);
      setKeywords(kw.data);
      setSearchTerms(st.data);
      setRecs(r.data);
      setDraft(d.data);
      setActions(act.data);
      setGates(g.data);
    } catch (err) {
      toast.error(`Laden mislukt: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onSeed = async () => {
    if (!window.confirm('Mock-data opnieuw seeden? Dit overschrijft bestaande mock-rijen (alleen source="mock").')) return;
    try {
      setBusy(true);
      const res = await axios.post(`${API}/admin/ads-control/seed-mock`, { days: 7, daily_budget_eur: 10 }, { headers });
      toast.success(`Seed klaar: ${res.data.daily_rows} dagen-rijen, ${res.data.search_terms} search terms`);
      await fetchAll();
    } catch (err) {
      toast.error(`Seed mislukt: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  const onRebuildRecs = async () => {
    try {
      setBusy(true);
      const res = await axios.post(`${API}/admin/ads-control/recommendations/rebuild`, {}, { headers });
      toast.success(`${res.data.inserted} nieuwe recommendations`);
      await fetchAll();
    } catch (err) {
      toast.error(`Rebuild mislukt: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  const onApprove = async (rec) => {
    if (!window.confirm(`Gesimuleerd goedkeuren?\n\nGEEN echte Google Ads-mutatie wordt uitgevoerd.\n\nRegel: ${rec.rule_id}\nActie: ${rec.type}`)) return;
    try {
      setBusy(true);
      await axios.post(`${API}/admin/ads-control/recommendations/${rec.id}/approve`, {}, { headers });
      toast.success('Goedgekeurd (simulated). Audit log bijgewerkt.');
      await fetchAll();
    } catch (err) {
      toast.error(`Approve mislukt: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  const onReject = async (rec) => {
    const reason = window.prompt('Reden voor afwijzing (optioneel):', '');
    if (reason === null) return;
    try {
      setBusy(true);
      await axios.post(`${API}/admin/ads-control/recommendations/${rec.id}/reject`, { reason }, { headers });
      toast.success('Afgewezen.');
      await fetchAll();
    } catch (err) {
      toast.error(`Reject mislukt: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  const onSimulatePublish = async () => {
    if (!window.confirm('Gesimuleerde publish? GEEN echte Google Ads-call wordt gedaan — alleen een audit-log entry.')) return;
    try {
      setBusy(true);
      const res = await axios.post(`${API}/admin/ads-control/draft/simulate-publish`, {}, { headers });
      toast.success(res.data.note);
      await fetchAll();
    } catch (err) {
      toast.error(`Simulate publish mislukt: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  if (loading || !health || !summary) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="ads-control-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const setup = health.setup;
  const cp = summary.cost_per || {};
  const totals = summary.totals_7d || {};

  return (
    <div className="space-y-6" data-testid="ads-control-agent-tab">
      {/* HEADER */}
      <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="ads-control-header">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-light text-foreground">Ads Control Agent</h2>
            <span className="text-[10px] uppercase tracking-wider rounded-full bg-slate-900 text-white px-2 py-0.5">MVP</span>
            <span className="text-[10px] uppercase tracking-wider rounded-full bg-amber-200 text-amber-900 px-2 py-0.5" data-testid="ads-phase-badge">
              phase: {health.phase}
            </span>
            <span className="text-[10px] uppercase tracking-wider rounded-full bg-slate-100 text-slate-800 px-2 py-0.5" data-testid="ads-source-badge">
              source: {health.data_source}
            </span>
            <span className="text-[10px] uppercase tracking-wider rounded-full bg-rose-100 text-rose-800 px-2 py-0.5" data-testid="ads-dryrun-badge">
              dry_run: {String(health.dry_run_mode)}
            </span>
            <span className="text-[10px] uppercase tracking-wider rounded-full bg-rose-100 text-rose-800 px-2 py-0.5">
              mutations: off
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border bg-white text-xs hover:bg-slate-50 disabled:opacity-50" data-testid="ads-refresh-btn">
              <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Vernieuw
            </button>
            <button onClick={onSeed} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-white text-xs hover:opacity-90 disabled:opacity-50" data-testid="ads-seed-btn">
              <Database className="w-3 h-3" /> Seed mock data
            </button>
            <button onClick={onRebuildRecs} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs hover:opacity-90 disabled:opacity-50" data-testid="ads-rebuild-recs-btn">
              <Play className="w-3 h-3" /> Recommendations herbouwen
            </button>
          </div>
        </div>
        <div className="text-xs text-amber-900 leading-relaxed">
          <strong>Veiligheid:</strong> Deze agent draait volledig op mock-data. Er worden GEEN echte Google Ads API-calls gedaan,
          GEEN budgetten/keywords/campagnes gemuteerd. Alle approve-acties registreren alleen een audit-entry met <code>dry_run=true</code>.
        </div>
      </div>

      {/* SETUP STATUS */}
      <div className="rounded-2xl border border-border bg-white p-4 space-y-2" data-testid="ads-setup-status">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-sm font-medium">Google Ads API setup-status</h3>
          <span className={`text-[10px] uppercase rounded-full px-2 py-0.5 ${setup.api_available ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
            {setup.api_available ? 'ready' : 'missing credentials'}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
          {['developer_token', 'oauth_client', 'refresh_token', 'customer_id', 'login_customer_id'].map((k) => (
            <div key={k} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-1.5">
              <span className="font-mono text-[11px]">{k}</span>
              <span className={setup[k] === 'present' ? 'text-emerald-700' : 'text-rose-700'}>{setup[k]}</span>
            </div>
          ))}
        </div>
        {setup.missing_env_vars?.length > 0 && (
          <div className="text-[11px] text-muted-foreground">
            Mist: <code>{setup.missing_env_vars.join(', ')}</code> — zet deze in <code>backend/.env</code> zodra de user dev-token + OAuth heeft.
          </div>
        )}
      </div>

      {/* KPI CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ads-kpi-cards">
        <StatBox label="Spend vandaag" value={eur(summary.spend_today_eur)} testId="ads-kpi-spend-today" />
        <StatBox label="Spend 7d" value={eur(summary.spend_7d_eur)} testId="ads-kpi-spend-7d" />
        <StatBox label="Clicks 7d" value={totals.clicks ?? 0} hint={`${totals.impressions ?? 0} impressies`} testId="ads-kpi-clicks" />
        <StatBox label="Paid 7d" value={totals.payment_completed ?? 0} hint={`${totals.intake_started ?? 0} intakes gestart`} testId="ads-kpi-paid" />
      </div>

      {/* COST PER FUNNEL STAGE */}
      <div className="rounded-2xl border border-border bg-white p-4" data-testid="ads-cost-per-funnel">
        <h3 className="text-sm font-medium mb-3">Cost per funnel-stap (7d)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {[
            ['intake_started', 'Intake gestart'],
            ['step0_completed', 'Step 0 goal'],
            ['step1_completed', 'Step 1 solar'],
            ['intake_completed', 'Intake klaar'],
            ['product_cta_clicked', 'Product CTA'],
            ['checkout_started', 'Checkout'],
            ['payment_completed', 'Paid'],
          ].map(([k, label]) => (
            <div key={k} className="bg-slate-50 rounded-lg p-2">
              <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
              <div className="font-mono">{cp[`${k}_eur`] != null ? eur(cp[`${k}_eur`]) : '—'}</div>
              <div className="text-[10px] text-muted-foreground">n={totals[k] ?? 0}</div>
            </div>
          ))}
        </div>
      </div>

      {/* HEALTH SCORES */}
      <div className="rounded-2xl border border-border bg-white p-4" data-testid="ads-health-scores">
        <h3 className="text-sm font-medium mb-3">Health scores per niveau (7d)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-muted-foreground border-b border-border">
              <tr>
                <th className="py-2 pr-2">Niveau</th>
                <th className="py-2 pr-2">Entity</th>
                <th className="py-2 pr-2 text-right">Spend</th>
                <th className="py-2 pr-2 text-right">Clicks</th>
                <th className="py-2 pr-2 text-right">Intake</th>
                <th className="py-2 pr-2 text-right">Paid</th>
                <th className="py-2 pr-2 text-right">Score</th>
                <th className="py-2 pr-2">Verdict</th>
              </tr>
            </thead>
            <tbody>
              {healthScores.items.map((row) => (
                <tr key={`${row.level}-${row.entity_id}`} className="border-b border-border last:border-b-0">
                  <td className="py-1.5 pr-2 font-mono text-[10px]">{row.level}</td>
                  <td className="py-1.5 pr-2">{row.entity_name}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{eur(row.spend_eur)}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{row.clicks}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{row.intake_started}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{row.payment_completed}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{row.score}</td>
                  <td className="py-1.5 pr-2">
                    <span className={`text-[10px] uppercase rounded-full px-2 py-0.5 ${VERDICT_BADGE[row.verdict] || ''}`}>{row.verdict}</span>
                  </td>
                </tr>
              ))}
              {healthScores.items.length === 0 && (
                <tr><td colSpan={8} className="py-4 text-center text-muted-foreground">Geen data — klik op "Seed mock data".</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* KEYWORDS — winners + losers */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-white p-4" data-testid="ads-keywords-table">
          <h3 className="text-sm font-medium mb-3">Top keywords (op spend)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground border-b border-border">
                <tr className="text-left">
                  <th className="py-1.5 pr-2">Keyword</th>
                  <th className="py-1.5 pr-2">Match</th>
                  <th className="py-1.5 pr-2 text-right">Spend</th>
                  <th className="py-1.5 pr-2 text-right">CTR</th>
                  <th className="py-1.5 pr-2 text-right">Step 0</th>
                </tr>
              </thead>
              <tbody>
                {keywords.items.slice(0, 12).map((kw) => (
                  <tr key={kw.criterion_id} className="border-b border-border last:border-b-0">
                    <td className="py-1 pr-2 font-mono text-[11px]">{kw.keyword_text}</td>
                    <td className="py-1 pr-2 text-[10px]">{kw.match_type}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{eur(kw.cost_eur)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{kw.ctr_pct}%</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{kw.step0_completed}</td>
                  </tr>
                ))}
                {keywords.items.length === 0 && (
                  <tr><td colSpan={5} className="py-3 text-center text-muted-foreground">—</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-white p-4" data-testid="ads-search-terms-table">
          <h3 className="text-sm font-medium mb-3">Search terms (top 12)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground border-b border-border">
                <tr className="text-left">
                  <th className="py-1.5 pr-2">Term</th>
                  <th className="py-1.5 pr-2">Ad-group</th>
                  <th className="py-1.5 pr-2 text-right">Spend</th>
                  <th className="py-1.5 pr-2 text-right">Intakes</th>
                </tr>
              </thead>
              <tbody>
                {searchTerms.items.slice(0, 12).map((st) => (
                  <tr key={st.id} className="border-b border-border last:border-b-0">
                    <td className="py-1 pr-2 font-mono text-[11px]">{st.search_term}</td>
                    <td className="py-1 pr-2 text-[10px]">{st.ad_group_name}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{eur(st.cost_eur)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">{st.intake_started}</td>
                  </tr>
                ))}
                {searchTerms.items.length === 0 && (
                  <tr><td colSpan={4} className="py-3 text-center text-muted-foreground">—</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* RECOMMENDATIONS */}
      <div className="rounded-2xl border border-border bg-white p-4 space-y-3" data-testid="ads-recommendations">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Pending recommendations</h3>
          <span className="text-xs text-muted-foreground">{recs.items.filter(r => r.status === 'pending').length} pending · {recs.items.filter(r => r.status === 'approved_simulated').length} approved · {recs.items.filter(r => r.status === 'rejected').length} rejected</span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {recs.items.map((rec) => (
            <RecommendationCard key={rec.id} rec={rec} onApprove={onApprove} onReject={onReject} busy={busy} />
          ))}
          {recs.items.length === 0 && (
            <div className="text-xs text-muted-foreground py-3">Nog geen recommendations. Seed eerst mock-data.</div>
          )}
        </div>
      </div>

      {/* ADS V2 DRAFT */}
      {draft && (
        <div className="rounded-2xl border border-border bg-white p-4 space-y-3" data-testid="ads-draft-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-medium">Ads V2 Draft Campaign</h3>
              <span className="text-[10px] uppercase rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">{draft.publish_status}</span>
            </div>
            <button onClick={onSimulatePublish} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs hover:opacity-90 disabled:opacity-50" data-testid="ads-draft-simulate-publish-btn">
              <FileWarning className="w-3 h-3" /> Simuleer publish (geen echte call)
            </button>
          </div>
          <div className="text-sm font-medium">{draft.campaign_name}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="bg-slate-50 rounded-lg p-2">
              <div className="text-[10px] uppercase text-muted-foreground">Daily budget</div>
              <div className="font-mono">{eur(draft.campaign_settings?.daily_budget_eur)}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <div className="text-[10px] uppercase text-muted-foreground">Bid strategy</div>
              <div className="font-mono">{draft.campaign_settings?.bid_strategy}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <div className="text-[10px] uppercase text-muted-foreground">Max CPC</div>
              <div className="font-mono">{eur(draft.campaign_settings?.max_cpc_eur)}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2">
              <div className="text-[10px] uppercase text-muted-foreground">Locatie / taal</div>
              <div className="font-mono">{draft.campaign_settings?.location} / {draft.campaign_settings?.language}</div>
            </div>
          </div>
          <div className="space-y-2">
            {(draft.ad_groups || []).map((ag) => (
              <div key={ag.utm_content} className="border border-border rounded-lg p-3" data-testid={`ads-draft-ag-${ag.utm_content}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-medium text-sm">{ag.name}</div>
                  <div className="flex gap-1">
                    <span className="text-[10px] uppercase rounded-full bg-slate-100 text-slate-700 px-2 py-0.5">intent: {ag.intent}</span>
                    <span className={`text-[10px] uppercase rounded-full px-2 py-0.5 ${ag.enabled_for_publish ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
                      {ag.enabled_for_publish ? 'publish-ready' : 'on hold'}
                    </span>
                  </div>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {ag.keywords_exact?.length || 0} exact · {ag.keywords_phrase?.length || 0} phrase · {ag.negatives?.length || 0} negatives · {ag.rsa?.headlines?.length || 0} headlines · {ag.rsa?.descriptions?.length || 0} descriptions
                </div>
              </div>
            ))}
          </div>
          {draft.warnings && (
            <div className="text-[11px] text-amber-700 space-y-1">
              {draft.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {w}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* AUTOPILOT GATES */}
      {gates && (
        <div className="rounded-2xl border border-border bg-white p-4" data-testid="ads-autopilot-gates">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium">Autopilot safety gates</h3>
            <span className={`text-[10px] uppercase rounded-full px-2 py-0.5 ${gates.autopilot_active ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
              autopilot: {gates.autopilot_active ? 'on' : 'off'}
            </span>
          </div>
          <GateRow label="Paid customers" required={gates.gate_paid_customers.required} ok={gates.gate_paid_customers.pass} current={`paid=${gates.gate_paid_customers.current_paid}, intakes=${gates.gate_paid_customers.current_intake_completed}`} testId="ads-gate-paid" />
          <GateRow label="Tracking health" required={gates.gate_tracking_health.required} ok={gates.gate_tracking_health.pass} current={gates.gate_tracking_health.current} testId="ads-gate-tracking" />
          <GateRow label="Stabiel data-window" required={gates.gate_stable_data_window.required} ok={gates.gate_stable_data_window.pass} current={gates.gate_stable_data_window.current} testId="ads-gate-window" />
          <GateRow label="Admin per-rule toggle" required={gates.gate_admin_toggle.required} ok={gates.gate_admin_toggle.pass} current={gates.gate_admin_toggle.current} testId="ads-gate-toggle" />
          <GateRow label="ADS_AGENT_DRY_RUN_MODE off" required={gates.gate_dry_run_off.required} ok={gates.gate_dry_run_off.pass} current={gates.gate_dry_run_off.current} testId="ads-gate-dryrun" />
          <div className="mt-3 flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg p-3">
            <Ban className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
            <div className="text-[11px] text-rose-800">
              Autopilot master kill-switch is <strong>{gates.autopilot_master_kill ? 'AAN (hard-uit)' : 'UIT'}</strong>. Zelfs als alle gates groen zijn, blijft autopilot in MVP geblokkeerd.
            </div>
          </div>
          <button disabled className="mt-3 w-full inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-rose-100 text-rose-700 text-xs cursor-not-allowed" data-testid="ads-emergency-stop-btn">
            <Ban className="w-3 h-3" /> Emergency stop (disabled in MVP — autopilot is al hard-off)
          </button>
        </div>
      )}

      {/* ACTION HISTORY */}
      <div className="rounded-2xl border border-border bg-white p-4" data-testid="ads-action-history">
        <h3 className="text-sm font-medium mb-3">Audit log (laatste {actions.items.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-b border-border">
              <tr className="text-left">
                <th className="py-1.5 pr-2">Tijd</th>
                <th className="py-1.5 pr-2">Actie</th>
                <th className="py-1.5 pr-2">Door</th>
                <th className="py-1.5 pr-2">Resultaat</th>
                <th className="py-1.5 pr-2">Dry-run</th>
              </tr>
            </thead>
            <tbody>
              {actions.items.map((a) => (
                <tr key={a.id} className="border-b border-border last:border-b-0">
                  <td className="py-1 pr-2 font-mono text-[10px]">{a.executed_at}</td>
                  <td className="py-1 pr-2">{a.action_type}</td>
                  <td className="py-1 pr-2 text-[11px]">{a.triggered_by}</td>
                  <td className="py-1 pr-2">{a.result}</td>
                  <td className="py-1 pr-2">{String(a.dry_run)}</td>
                </tr>
              ))}
              {actions.items.length === 0 && (
                <tr><td colSpan={5} className="py-3 text-center text-muted-foreground">Nog geen acties uitgevoerd.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
